import React, { createContext, useContext, useReducer } from 'react';
import type { ReactNode } from 'react';

export const FS_SCREENS = {
    WELCOME: "WELCOME",
    CHECKLIST_INFO: "CHECKLIST_INFO",
    PPE_CHECKLIST: "PPE_CHECKLIST",
    GMP_CHECKLIST: "GMP_CHECKLIST",
    PCI_CHECKLIST: "PCI_CHECKLIST",
    SUMMARY: "SUMMARY",
    DASHBOARD: "DASHBOARD"
} as const;

export type FS_SCREENS = typeof FS_SCREENS[keyof typeof FS_SCREENS];

export interface FSSessionData {
    site: string;
    line: string;
    qaExecutive: string;
    productionIncharge: string;
    areaIncharge?: string;
    area?: string;
    cycle: string;
    tourStartDate: string;
}

interface FSState {
    currentScreen: FS_SCREENS;
    selectedChecklistType: string;
    selectedShift: string;
    currentTourId: string | null;
    sessionData: FSSessionData | null;
    isLoading: boolean;
    isPaused: boolean;
}

type FSAction =
    | { type: 'SET_SCREEN'; payload: FS_SCREENS }
    | { type: 'SET_SELECTIONS'; payload: { type: string, shift: string } }
    | { type: 'SET_TOUR_ID'; payload: string }
    | { type: 'SET_SESSION_DATA'; payload: FSSessionData }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_IS_PAUSED'; payload: boolean }
    | { type: 'RESET_STATE' };

const initialState: FSState = {
    currentScreen: FS_SCREENS.CHECKLIST_INFO,
    selectedChecklistType: "PPE Checklist",
    selectedShift: "Shift 1",
    currentTourId: null,
    sessionData: null,
    isLoading: false,
    isPaused: false,
};

function fsReducer(state: FSState, action: FSAction): FSState {
    switch (action.type) {
        case 'SET_SCREEN':
            return { ...state, currentScreen: action.payload };
        case 'SET_SELECTIONS':
            return { ...state, selectedChecklistType: action.payload.type, selectedShift: action.payload.shift };
        case 'SET_TOUR_ID':
            return { ...state, currentTourId: action.payload };
        case 'SET_SESSION_DATA':
            return { ...state, sessionData: action.payload };
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };
        case 'SET_IS_PAUSED':
            return { ...state, isPaused: action.payload };
        case 'RESET_STATE':
            return { ...initialState };
        default:
            return state;
    }
}

interface FSContextType {
    state: FSState;
    dispatch: React.Dispatch<FSAction>;
}

const FoodSafetyContext = createContext<FSContextType | undefined>(undefined);

export const FoodSafetyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(fsReducer, initialState);

    return (
        <FoodSafetyContext.Provider value={{ state, dispatch }}>
            {children}
        </FoodSafetyContext.Provider>
    );
};

export const useFoodSafetyContext = () => {
    const context = useContext(FoodSafetyContext);
    if (!context) {
        throw new Error('useFoodSafetyContext must be used within a FoodSafetyProvider');
    }
    return context;
};
